package com.rosario.juridico.ui.screens

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.rosario.juridico.openEmail
import com.rosario.juridico.openPhone
import com.rosario.juridico.openUrl
import com.rosario.juridico.domain.Dependencia
import com.rosario.juridico.domain.DependenciaRepository

import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.ui.platform.LocalUriHandler
import kotlinx.coroutines.delay

@Composable
fun PantallaGuiaInfo(focusId: String? = null) {
    var solicitud by remember { mutableStateOf("") }
    var todasLasDependencias by remember { mutableStateOf<List<Dependencia>>(emptyList()) }
    var resultados by remember { mutableStateOf<List<Dependencia>>(emptyList()) }
    
    // Estado de expansión tipo Acordeón: Set de nombres de categorías expandidas
    var categoriasExpandidas by rememberSaveable { mutableStateOf(setOf<String>()) }
    
    val listState = rememberLazyListState()
    val scope = rememberCoroutineScope()

    // Carga inicial de datos
    LaunchedEffect(Unit) {
        todasLasDependencias = DependenciaRepository.getDependencias()
        
        // Lógica de auto-expansión si venimos desde el buscador
        if (focusId != null) {
            val dep = todasLasDependencias.find { it.id == focusId }
            if (dep != null) {
                // 1. Abrir la categoría
                categoriasExpandidas = categoriasExpandidas + dep.categoria
                
                // 2. Esperar a que el LazyColumn se recomponga con los nuevos ítems
                delay(300) 
                
                // 3. Buscar el índice global para el scroll
                // El índice es un poco complejo de calcular en LazyColumn con secciones
                // Una forma simple es buscar en el árbol de categorías
            }
        }
    }

    // Efecto separado para el scroll una vez que la categoría está expandida y los datos cargados
    LaunchedEffect(categoriasExpandidas, todasLasDependencias) {
        if (focusId != null && todasLasDependencias.isNotEmpty()) {
            val dep = todasLasDependencias.find { it.id == focusId }
            if (dep != null && categoriasExpandidas.contains(dep.categoria)) {
                // Cálculo de índice: (Categorías anteriores + sus items) + (Esta categoría + ítems hasta el nuestro)
                var targetIndex = 1 // Empezamos en 1 por el Header/Buscador y el label
                
                val dependenciasPorCategoria = todasLasDependencias.groupBy { it.categoria }
                
                for ((cat, items) in dependenciasPorCategoria) {
                    targetIndex++ // El Header de la categoría
                    if (cat == dep.categoria) {
                        val itemIndexInCat = items.indexOfFirst { it.id == focusId }
                        targetIndex += itemIndexInCat + 1 // +1 para saltar el Header de esta categoría
                        break
                    } else {
                        // Si la categoría estaba abierta, sumamos sus ítems, si no, solo el header
                        if (categoriasExpandidas.contains(cat)) {
                            targetIndex += items.size
                        }
                    }
                }
                
                delay(100) // Pequeño delay extra para asegurar layout
                listState.animateScrollToItem(targetIndex)
            }
        }
    }

    // Búsqueda reactiva
    LaunchedEffect(solicitud, todasLasDependencias) {
        resultados = if (solicitud.isBlank()) {
            emptyList()
        } else {
            DependenciaRepository.buscar(solicitud)
        }
    }

    val dependenciasPorCategoria = remember(todasLasDependencias) {
        todasLasDependencias.groupBy { it.categoria }
    }

    LazyColumn(
        state = listState,
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background),
        contentPadding = PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        // Cabecera y Buscador
        item {
            PremiumHeader(
                icono = Icons.Default.LocationOn,
                titulo = "Guía de dependencias",
                subtitulo = "Directorio local de juzgados, cámaras, comisarías y organismos de Rosario",
            )
            Spacer(modifier = Modifier.height(16.dp))
            
            OutlinedTextField(
                value = solicitud,
                onValueChange = { solicitud = it },
                label = { Text("¿Qué dependencia buscás?") },
                placeholder = { Text("Ej: Tribunales, Policía, CTD, Federal...") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                leadingIcon = { Icon(Icons.Default.Search, contentDescription = null) },
            )
            Spacer(modifier = Modifier.height(8.dp))
        }

        // CASO A: Modo Búsqueda (Lista Plana, ignora colapsables)
        if (solicitud.isNotBlank()) {
            item {
                Text(
                    text = "${resultados.size} resultado(s) encontrados",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }

            if (resultados.isEmpty()) {
                item {
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
                    ) {
                        Text(
                            text = "No hubo coincidencias. Intentá con términos generales.",
                            style = MaterialTheme.typography.bodySmall,
                            modifier = Modifier.padding(14.dp),
                        )
                    }
                }
            } else {
                items(resultados) { dep ->
                    DependenciaItemCard(dep)
                }
            }
        } 
        // CASO B: Modo Directorio (Acordeón colapsable)
        else {
            item {
                SeccionGuiaLabel("Directorio por áreas")
            }

            dependenciasPorCategoria.forEach { (categoria, items) ->
                val isExpanded = categoriasExpandidas.contains(categoria)
                
                item(key = categoria) {
                    CategoryAccordionHeader(
                        titulo = categoria,
                        cantidad = items.size,
                        isExpanded = isExpanded,
                        onToggle = {
                            categoriasExpandidas = if (isExpanded) {
                                categoriasExpandidas - categoria
                            } else {
                                categoriasExpandidas + categoria
                            }
                        }
                    )
                }

                if (isExpanded) {
                    items(items, key = { it.id }) { dep ->
                        DependenciaItemCard(dep)
                    }
                }
            }
        }

        item {
            Spacer(modifier = Modifier.height(32.dp))
        }
    }
}

@Composable
private fun CategoryAccordionHeader(
    titulo: String,
    cantidad: Int,
    isExpanded: Boolean,
    onToggle: () -> Unit
) {
    val rotationState by animateFloatAsState(targetValue = if (isExpanded) 180f else 0f)

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onToggle() }
            .padding(top = 8.dp)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.padding(vertical = 8.dp)
        ) {
            Icon(
                imageVector = when {
                    titulo.contains("Penal", true) || titulo.contains("MPA", true) -> Icons.Default.Balance
                    titulo.contains("Policía", true) || titulo.contains("Comisarías", true) -> Icons.Default.Shield
                    titulo.contains("Mediación", true) -> Icons.Default.Groups
                    titulo.contains("Federal", true) -> Icons.Default.AccountBalance
                    else -> Icons.Default.Domain
                },
                contentDescription = null,
                tint = if (isExpanded) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.size(20.dp)
            )
            Spacer(Modifier.width(12.dp))
            
            Text(
                text = "${titulo.uppercase()} ($cantidad)",
                style = MaterialTheme.typography.titleSmall,
                fontWeight = if (isExpanded) FontWeight.ExtraBold else FontWeight.Bold,
                color = if (isExpanded) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant,
                letterSpacing = 0.5.sp,
                modifier = Modifier.weight(1f)
            )

            Icon(
                imageVector = Icons.Default.KeyboardArrowDown,
                contentDescription = null,
                tint = if (isExpanded) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier
                    .size(24.dp)
                    .rotate(rotationState)
            )
        }
        
        HorizontalDivider(
            thickness = if (isExpanded) 2.dp else 1.dp,
            color = if (isExpanded) MaterialTheme.colorScheme.primary.copy(alpha = 0.3f) 
                    else MaterialTheme.colorScheme.outlineVariant
        )
    }
}

@Composable
private fun SeccionGuiaLabel(texto: String) {
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth().padding(bottom = 4.dp)) {
        Box(modifier = Modifier.width(4.dp).height(18.dp).background(MaterialTheme.colorScheme.secondary))
        Text(text = texto, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, modifier = Modifier.padding(start = 8.dp))
    }
}

@Composable
private fun DependenciaItemCard(dep: Dependencia) {
    val uriHandler = LocalUriHandler.current

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 4.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        border = CardDefaults.outlinedCardBorder(),
    ) {
        Column(modifier = Modifier.padding(14.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text(text = dep.nombre, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Bold)

            ActionRow(
                icon = Icons.Default.LocationOn,
                text = dep.direccion,
                onClick = { openUrl("https://www.google.com/maps/search/?api=1&query=${dep.direccion.replace(" ", "+")}") }
            )

            dep.telefonos.forEach { tel ->
                if (tel.isNotBlank()) {
                    ActionRow(icon = Icons.Default.Phone, text = tel.trim(), onClick = { openPhone(tel.trim()) })
                }
            }

            if (dep.email.isNotBlank()) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.fillMaxWidth().padding(vertical = 2.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.Email,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.size(16.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = dep.email,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.clickable {
                            if (dep.email.isNotBlank()) {
                                uriHandler.openUri("mailto:${dep.email}")
                            }
                        }
                    )
                }
            }

            if (dep.descripcion.isNotBlank()) {
                HorizontalDivider(modifier = Modifier.padding(top = 4.dp), thickness = 0.5.dp, color = MaterialTheme.colorScheme.outlineVariant)
                Text(
                    text = dep.descripcion,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    lineHeight = 16.sp
                )
            }
        }
    }
}

@Composable
private fun ActionRow(icon: ImageVector, text: String, onClick: () -> Unit) {
    Row(
        verticalAlignment = Alignment.CenterVertically, 
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 2.dp)
    ) {
        Icon(imageVector = icon, contentDescription = null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(16.dp))
        Spacer(modifier = Modifier.width(8.dp))
        Text(text = text, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.primary)
    }
}
